import httpClient from 'api/httpClient';
import { AxiosRequestConfig } from 'axios';
import { DropzoneFile } from 'components/Dropzone/utils';
import { FilePreview, UploadedFileResponse } from 'models/File';
import {
  ChatSessionFile,
  MessageLocation,
  MessageType,
  SessionType,
  UserConnection,
} from 'models/Message';
import { PaginationParams, PaginatedData } from 'models/PaginatedData';
import { ReactionType } from 'models/Reactions';
import { getFileParams, getUploadedFile } from 'uploadFileUtils';

const sendMesssage = (
  sessionId: string,
  text: string,
  uploadedFiles: DropzoneFile[],
  messageLocation: MessageLocation,
) => {
  return httpClient.post<
    MessageType & { createdChatSessionFiles: ChatSessionFile[] }
  >(`/chats/sessions/${sessionId}/messages`, {
    text,
    uploadedFiles,
    messageLocation,
  });
};

const getSessions = (
  params: PaginationParams & { openSessionId?: string; listingId?: string },
) => {
  return httpClient.get<PaginatedData<SessionType>>('/chats/sessions', {
    params,
  });
};

const getSessionById = (sessionId: string) => {
  return httpClient.get<SessionType>(`/chats/sessions/${sessionId}`);
};

const getUserConnections = (
  params: PaginationParams & { listingId?: string },
) => {
  return httpClient.get<PaginatedData<UserConnection>>(
    '/chats/user-connections',
    {
      params,
    },
  );
};

const getSessionMessages = (params: PaginationParams, sessionId: string) => {
  return httpClient.get<PaginatedData<MessageType>>(
    `/chats/sessions/${sessionId}/messages`,
    {
      params,
    },
  );
};

const readAllMessages = (sessionId: string) => {
  return httpClient.post(`/chats/sessions/read`, {
    sessionId,
  });
};

const unreadAllMessages = (sessionId: string) => {
  return httpClient.post(`/chats/sessions/unread`, {
    sessionId,
  });
};

const deleteMessage = (messageId: string) => {
  return httpClient.delete(`/chats/messages/${messageId}`);
};

const moveFileToDataRoom = (sessionId: string, fileId: string) => {
  return httpClient.post(
    `/chats/sessions/${sessionId}/files/${fileId}/move-to-data-room`,
  );
};

const getMessagesFiles = (sessionId: string, params: PaginationParams) => {
  return httpClient.get<PaginatedData<ChatSessionFile>>(
    `/chats/sessions/${sessionId}/files`,
    { params },
  );
};

const previewMessageFile = (sessionId: string, fileId: string) => {
  return httpClient.get<FilePreview>(
    `/chats/sessions/${sessionId}/files/${fileId}/preview`,
  );
};

const removeMessageFile = (
  sessionId: string,
  fileId: string,
  deleteFromDataRoom?: boolean,
) => {
  return httpClient.post(
    `/chats/sessions/${sessionId}/files/${fileId}/delete-file`,
    {
      deleteFromDataRoom,
    },
  );
};

async function uploadFile(
  sessionId: string,
  file: File,
  config?: AxiosRequestConfig,
): Promise<DropzoneFile | null> {
  const fileParams = getFileParams('chatFile', file);

  const { data } = await httpClient.post<UploadedFileResponse>(
    `/chats/sessions/${sessionId}/upload-temporary-object`,
    fileParams,
  );

  return await getUploadedFile(data, file, config);
}

function broadcastMessage(
  dealRoomId: string,
  text: string,
  sendInvitationEmail: boolean,
) {
  return httpClient.post(`chats/deal-rooms/${dealRoomId}/broadcast-message`, {
    text,
    sendInvitationEmail,
  });
}

const addMessageReaction = (messageId: string, reactionType: ReactionType) => {
  return httpClient.post<MessageType>(`chats/messages/${messageId}/reaction`, {
    reactionType,
  });
};

export default {
  sendMesssage,
  getSessions,
  getSessionById,
  getSessionMessages,
  getUserConnections,
  readAllMessages,
  unreadAllMessages,
  deleteMessage,
  moveFileToDataRoom,
  getMessagesFiles,
  uploadFile,
  previewMessageFile,
  removeMessageFile,
  broadcastMessage,
  addMessageReaction,
};
